import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { IconArrowRight, IconChevronDown, IconChevronUp, IconClock } from '@tabler/icons-react';
import { TeeButton } from '@/components/ui/tee-button';
import { issueNoticeService } from '@/services/issue-notice.service';
import type { IssueNoticePublic } from '@/types/issue-notice';
import { IssueCountdown } from './IssueCountdown';
import { PublicIssueDetailModal } from './PublicIssueDetailModal';
import { SEVERITY_META } from './issue-notice-meta';
import { cn } from '@/utils/cn';

interface RowProps {
  notice: IssueNoticePublic;
  serverOffsetMs: number;
  solid?: boolean;
  onOpen: (id: string) => void;
}

function PublicRow({ notice, serverOffsetMs, solid, onOpen }: RowProps) {
  const { t } = useTranslation();
  const meta = SEVERITY_META[notice.severity];
  const { Icon } = meta;
  return (
    <div className={cn('flex min-w-0 items-center gap-3', !solid && 'py-2')}>
      <span
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold uppercase tracking-[0.06em]"
        style={solid ? { color: meta.fg, backgroundColor: 'var(--text-on-severity)' } : { color: meta.fg, backgroundColor: meta.bg }}
      >
        <Icon size={16} aria-hidden />
        <span className="hidden sm:inline">{t(`issues.severity.${notice.severity}`)}</span>
        <span className="sr-only sm:hidden">{t(`issues.severity.${notice.severity}`)}</span>
      </span>
      <span
        title={notice.title}
        className="min-w-0 flex-1 truncate text-[15px] font-semibold"
        style={{ color: solid ? 'var(--text-on-severity)' : 'var(--text-content-title)' }}
      >
        {notice.title}
      </span>
      <span
        className="hidden sm:inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-semibold tabular-nums"
        style={
          solid
            ? { color: 'var(--text-on-severity)', backgroundColor: 'var(--background-on-severity-chip)' }
            : { color: 'var(--text-content-title)', backgroundColor: 'var(--background-page-header)' }
        }
      >
        <IconClock size={14} aria-hidden />
        <IssueCountdown target={notice.endsAt} serverOffsetMs={serverOffsetMs} />
      </span>
      <TeeButton
        variant="ghost"
        size="s"
        className="shrink-0 font-semibold underline underline-offset-2"
        style={{ color: solid ? 'var(--text-on-severity)' : 'var(--text-content-title)' }}
        onClick={() => onOpen(notice.id)}
        rightIcon={<IconArrowRight size={14} />}
        text={t('issues.viewDetails')}
      />
    </div>
  );
}

/** Sign-in page banner for all-location issues; details open read-only in a modal. */
export function PublicIssueBanner() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ['issue-notices', 'public-live'],
    queryFn: () => issueNoticeService.publicLive(),
    refetchInterval: 60_000,
    retry: false,
  });
  const serverOffsetMs = useMemo(() => (data ? Date.parse(data.serverNow) - Date.now() : 0), [data]);

  const active = data?.active ?? [];
  if (!active.length) return null;

  const [top, ...rest] = active;
  const role = top.severity === 'critical' ? 'alert' : 'status';

  return (
    <div className="flex-shrink-0" style={{ backgroundColor: SEVERITY_META[top.severity].fg }} data-testid="public-issue-banner">
      <div className="mx-auto w-full max-w-[var(--page-max-width)] px-4 md:px-10 py-3">
        <div role={role} aria-live={role === 'alert' ? 'assertive' : 'polite'} className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <PublicRow notice={top} serverOffsetMs={serverOffsetMs} solid onOpen={setOpenId} />
          </div>
          {rest.length > 0 && (
            <TeeButton
              variant="ghost"
              size="s"
              className="shrink-0 font-semibold"
              style={{ color: 'var(--text-on-severity)' }}
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              rightIcon={expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
              text={t('issues.moreIssues', { count: rest.length })}
            />
          )}
        </div>
        {expanded && rest.length > 0 && (
          <div className="mt-1 max-h-[40vh] overflow-y-auto divide-y divide-[var(--border-default)] rounded-[6px] bg-[var(--background-card)] px-3">
            {rest.map((n) => (
              <PublicRow key={n.id} notice={n} serverOffsetMs={serverOffsetMs} onOpen={setOpenId} />
            ))}
          </div>
        )}
      </div>
      <PublicIssueDetailModal noticeId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
